import React from 'react';

/**
 * Performance monitoring utilities for code splitting and lazy loading
 */

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

interface ChunkLoadMetric {
  chunkName: string;
  loadTime: number;
  size?: number;
  cached: boolean;
  timestamp: number;
}

interface RouteChangeMetric {
  from: string;
  to: string;
  loadTime: number;
  timestamp: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private chunkMetrics: ChunkLoadMetric[] = [];
  private routeMetrics: RouteChangeMetric[] = [];
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initializeObservers();
    this.monitorWebVitals();
  }

  private initializeObservers(): void {
    // Monitor navigation timing
    if ('PerformanceObserver' in window) {
      const navObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            this.recordMetric('navigation', navEntry.loadEventEnd - navEntry.fetchStart, {
              type: navEntry.type,
              redirectCount: navEntry.redirectCount
            });
          }
        });
      });

      navObserver.observe({ entryTypes: ['navigation'] });
      this.observers.push(navObserver);

      // Monitor resource loading (chunks)
      const resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.name.includes('chunk') || entry.name.includes('.js')) {
            const resourceEntry = entry as PerformanceResourceTiming;
            this.recordChunkLoad(
              this.extractChunkName(entry.name),
              entry.duration,
              undefined,
              resourceEntry.transferSize === 0 // Cached if transfer size is 0
            );
          }
        });
      });

      resourceObserver.observe({ entryTypes: ['resource'] });
      this.observers.push(resourceObserver);
    }
  }

  private monitorWebVitals(): void {
    // Monitor Core Web Vitals
    if ('web-vitals' in window || typeof window !== 'undefined') {
      // FCP - First Contentful Paint
      this.observeWebVital('first-contentful-paint', (value) => {
        this.recordMetric('FCP', value);
      });

      // LCP - Largest Contentful Paint
      this.observeWebVital('largest-contentful-paint', (value) => {
        this.recordMetric('LCP', value);
      });

      // CLS - Cumulative Layout Shift
      this.observeWebVital('layout-shift', (value) => {
        this.recordMetric('CLS', value);
      });

      // FID - First Input Delay (when available)
      this.observeWebVital('first-input', (value) => {
        this.recordMetric('FID', value);
      });
    }
  }

  private observeWebVital(type: string, callback: (value: number) => void): void {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry: any) => {
            if (entry.entryType === type) {
              callback(entry.value || entry.duration || entry.startTime);
            }
          });
        });

        observer.observe({ entryTypes: [type] });
        this.observers.push(observer);
      } catch (error) {
        // Some browsers might not support all entry types
        console.warn(`Could not observe ${type}:`, error);
      }
    }
  }

  private extractChunkName(url: string): string {
    const match = url.match(/\/static\/js\/(.+?)\.chunk\.js/);
    return match ? match[1] : 'unknown';
  }

  /**
   * Record a performance metric
   */
  recordMetric(name: string, value: number, metadata?: Record<string, any>): void {
    this.metrics.push({
      name,
      value,
      timestamp: Date.now(),
      metadata
    });

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Performance: ${name} = ${value.toFixed(2)}ms`, metadata);
    }
  }

  /**
   * Record chunk loading performance
   */
  recordChunkLoad(chunkName: string, loadTime: number, size?: number, cached: boolean = false): void {
    this.chunkMetrics.push({
      chunkName,
      loadTime,
      size,
      cached,
      timestamp: Date.now()
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`Chunk loaded: ${chunkName} in ${loadTime.toFixed(2)}ms ${cached ? '(cached)' : ''}`);
    }
  }

  /**
   * Record route change performance
   */
  recordRouteChange(from: string, to: string, loadTime: number): void {
    this.routeMetrics.push({
      from,
      to,
      loadTime,
      timestamp: Date.now()
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`Route change: ${from} → ${to} in ${loadTime.toFixed(2)}ms`);
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    metrics: PerformanceMetric[];
    chunks: ChunkLoadMetric[];
    routes: RouteChangeMetric[];
    summary: {
      averageChunkLoadTime: number;
      totalChunksLoaded: number;
      cachedChunks: number;
      averageRouteChangeTime: number;
      coreWebVitals: Record<string, number>;
    };
  } {
    const coreWebVitals = this.metrics
      .filter(m => ['FCP', 'LCP', 'CLS', 'FID'].includes(m.name))
      .reduce((acc, metric) => {
        acc[metric.name] = metric.value;
        return acc;
      }, {} as Record<string, number>);

    return {
      metrics: this.metrics,
      chunks: this.chunkMetrics,
      routes: this.routeMetrics,
      summary: {
        averageChunkLoadTime: this.chunkMetrics.reduce((sum, chunk) => sum + chunk.loadTime, 0) / this.chunkMetrics.length || 0,
        totalChunksLoaded: this.chunkMetrics.length,
        cachedChunks: this.chunkMetrics.filter(chunk => chunk.cached).length,
        averageRouteChangeTime: this.routeMetrics.reduce((sum, route) => sum + route.loadTime, 0) / this.routeMetrics.length || 0,
        coreWebVitals
      }
    };
  }

  /**
   * Export performance data for analysis
   */
  exportPerformanceData(): string {
    return JSON.stringify(this.getPerformanceSummary(), null, 2);
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics = [];
    this.chunkMetrics = [];
    this.routeMetrics = [];
  }

  /**
   * Cleanup observers
   */
  cleanup(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Hook for monitoring component performance
 */
export function usePerformanceMonitor(componentName: string) {
  const startTime = React.useRef<number>(Date.now());

  React.useEffect(() => {
    startTime.current = Date.now();
  }, []);

  React.useEffect(() => {
    return () => {
      const renderTime = Date.now() - startTime.current;
      performanceMonitor.recordMetric(`component-${componentName}`, renderTime);
    };
  }, [componentName]);

  const recordCustomMetric = (metricName: string, value: number, metadata?: Record<string, any>) => {
    performanceMonitor.recordMetric(`${componentName}-${metricName}`, value, metadata);
  };

  return { recordCustomMetric };
}

/**
 * Higher-order component for performance monitoring
 */
export function withPerformanceMonitoring<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string
) {
  const MonitoredComponent = (props: P) => {
    const startTime = React.useRef<number>(Date.now());

    React.useEffect(() => {
      const renderTime = Date.now() - startTime.current;
      performanceMonitor.recordMetric(`component-${componentName}`, renderTime);
    });

    return React.createElement(WrappedComponent, props);
  };

  MonitoredComponent.displayName = `withPerformanceMonitoring(${componentName})`;
  return MonitoredComponent;
}